import React, { useMemo } from 'react';
import type { GuestSiteEventDesign } from '../../../layout.types';
import { brannanConfig, useBrannanDesign } from '../../LayoutBrannan.theme';
import { FrameInner, FrameOuter } from '../Frames';
import { Page } from '../Page';
import chroma from 'chroma-js';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated-base';
import { getCustomArtUrl, getIsCustomArtEnabled } from '../../../layout.utils';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { addRendition } from '@shared/utils/photoRendition';

const { layerBack, layerMiddle } = brannanConfig.applicatorHtmlProps;

// Extracted for unit-testability — keeps the mobile/desktop asset selection and rendition logic in one place.
export const getShellCustomArtProps = (eventDesign: GuestSiteEventDesign | undefined, isMobileOrTablet: boolean) => ({
  isCustomArtEnabled: getIsCustomArtEnabled(eventDesign),
  // The shell always uses the desktop theme asset (not the mobile one) so the body art matches the desktop layout.
  // On mobile we add rendition=medium to avoid downloading the full-res image.
  customArtUrl: isMobileOrTablet ? addRendition({ url: getCustomArtUrl(eventDesign), renditionSize: 'medium' }) : getCustomArtUrl(eventDesign, DesignLayoutType.brannan),
});

export interface ShellProps {
  eventDesign?: GuestSiteEventDesign;
  eventHandle: string;
  noTranslucency?: boolean;
  isAlohaGuestPage?: boolean;
  isCustomPage?: boolean;
  showTopPadding?: boolean;
  /**
   * When true, bypasses the style applicator loading state.
   * Use this for skeleton components that render before the style applicator
   * script is injected (e.g., RegistryShell during initial load).
   */
  bypassStyleApplicatorLoading?: boolean;
}

export const Shell: React.FC<ShellProps> = ({ eventHandle, eventDesign, isCustomPage, showTopPadding, bypassStyleApplicatorLoading, children }) => {
  const { applicatorConfig, loading: styleApplicatorLoading, CustomStyle } = useBrannanDesign(eventHandle, eventDesign);
  const loading = bypassStyleApplicatorLoading ? false : styleApplicatorLoading;
  const { pageColor, pageFontColor, artBackgroundColor } = useColorPaletteProvider();

  const isSinglePage = eventDesign?.websiteLayout.layoutType === DesignLayoutType.brannan && eventDesign?.brannanNavigation === BrannanNavigation.singlePage;

  // For some themes (e.g. Rustic Chic) custom background color is not being applied even if enabled
  // This workaround allows to override the default theme color
  const customBackgroundColorConfig = applicatorConfig?.layerBack.customBackgroundColor;
  const customBackgroundColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.backgroundColor
      ? chroma(customBackgroundColorConfig?.backgroundColor as Array<number>).hex()
      : undefined;

  const customTextColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.textColor ? chroma(customBackgroundColorConfig?.textColor as Array<number>).hex() : undefined;

  const isMobileOrTablet = useIsMobileOrTablet();
  const { isCustomArtEnabled, customArtUrl } = useMemo(() => getShellCustomArtProps(eventDesign, isMobileOrTablet), [eventDesign, isMobileOrTablet]);

  return (
    <FrameOuter
      loading={loading}
      applicatorProp={layerBack.prop}
      customBackgroundColor={customBackgroundColor}
      loadingBackgroundColor={artBackgroundColor}
      customTextColor={customTextColor}
      isCustomPage={isCustomPage}
      customArtUrl={customArtUrl}
      isCustomArtEnabled={isCustomArtEnabled}
    >
      {CustomStyle && <CustomStyle />}
      <FrameInner>
        <Page
          customBackgroundColor={pageColor}
          customTextColor={pageFontColor}
          applicatorProp={layerMiddle.prop}
          showBorder={applicatorConfig?.layerMiddle.border?.isEnabled}
          noTranslucency={!applicatorConfig?.layerMiddle.backgroundTranslucency?.isEnabled}
          isSinglePage={isSinglePage}
          showTopPadding={showTopPadding}
        >
          {children}
        </Page>
      </FrameInner>
    </FrameOuter>
  );
};
